import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Primitives";
import { Plus, Trash2, X, Star, Mail, ChevronLeft, ChevronUp, ChevronDown, Workflow as WorkflowIcon, Upload, Loader2, Download, Copy, ClipboardPaste, GripVertical } from "lucide-react";
import { RichTextEditor, MergeMenu } from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PROPERTY_TYPES = [["single_family", "Single Family"], ["condo", "Condo"], ["townhouse", "Townhouse"], ["multi_family", "Multi-Family"], ["land", "Land"]];

function CopyWorkflowDialog({ source, onClose, onCopied }) {
  const [f, setF] = useState({ name: `Copy of ${source.name}`, transaction_side: source.transaction_side || "", property_type: source.property_type || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tm/workflows", { ...f, transaction_side: f.transaction_side || null, property_type: f.property_type || null, description: source.description, task_groups: source.task_groups });
      toast.success("Workflow copied"); onCopied(data);
    } catch (err) { toast.error("Couldn't copy workflow"); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="copy-workflow-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Copy workflow</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><span className={lbl}>Name</span><input data-testid="copy-wf-name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><span className={lbl}>Side</span>
            <select data-testid="copy-wf-side" className={inp} value={f.transaction_side} onChange={(e) => setF({ ...f, transaction_side: e.target.value })}>
              <option value="">Not set</option>{SIDE_TABS.slice(1).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><span className={lbl}>Property type</span>
            <select data-testid="copy-wf-type" className={inp} value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}>
              <option value="">Not set</option>{PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <button data-testid="copy-wf-submit" disabled={busy || !f.name.trim()} onClick={submit} className="w-full py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] disabled:opacity-60">{busy ? "Copying…" : "Copy workflow"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SIDE_TABS = [["all", "All"], ["buyer", "Buyer"], ["seller", "Seller"], ["dual", "Dual"]];

const REF_DATES = [
  ["contract_date", "Contract"], ["closing_date", "Closing"], ["earnest_money_deadline", "Earnest Money"],
  ["inspection_deadline", "Inspection"], ["appraisal_deadline", "Appraisal"], ["financing_deadline", "Financing"],
  ["insurance_deadline", "Insurance"], ["possession_date", "Possession"],
];
const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
const lbl = "text-[11px] tracking-[0.1em] uppercase text-[#8a8a86] block mb-1";
let _uidc = 0;
const uid = () => `k${Date.now().toString(36)}${(_uidc++).toString(36)}`;
const blankTask = () => ({ _k: uid(), title: "", reference_date: "closing_date", days_offset: 0, due_direction: "before", is_key_date: false, is_email: false, description: "", email_subject: "", email_body: "", email_to: [], email_cc: [], email_bcc: [], email_cc_extra: "", email_bcc_extra: "" });

export const RECIPIENT_ROLES = [
  ["assigned_coordinator", "Assigned Coordinator"], ["manager", "Manager"],
  ["buyer1", "Buyer 1"], ["buyer2", "Buyer 2"], ["seller1", "Seller 1"], ["seller2", "Seller 2"],
  ["buyer_agent", "Buyer Agent"], ["seller_agent", "Seller Agent"],
  ["lender", "Lender"], ["title_company", "Title Company"], ["loan_processor", "Loan Processor"],
];

function RoleRow({ label, selected, onToggle, testid }) {
  return (
    <div>
      <div className={lbl}>{label}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid={testid}>
        {RECIPIENT_ROLES.map(([key, rlabel]) => (
          <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer text-[#575755]">
            <input type="checkbox" data-testid={`${testid}-${key}`} checked={(selected || []).includes(key)} onChange={() => onToggle(key)} className="accent-[#F7941D]" />
            <span>{rlabel}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Workflows() {
  const { user } = useAuth();
  const canWrite = (user?.tools || []).includes("transactions") || user?.role === "admin";
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sideFilter, setSideFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [copying, setCopying] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/tm/workflows"); setList(data); }, []);
  useEffect(() => { load(); }, [load]);

  const exportWorkflows = async () => {
    try {
      const res = await api.get("/tm/workflows/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `workflows-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/tm/workflows/import", fd);
      toast.success(`Imported ${data.total} workflow${data.total === 1 ? "" : "s"} (${data.total_tasks} tasks · ${data.created} new, ${data.updated} updated)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete workflow "${t.name}"?`)) return;
    await api.delete(`/tm/workflows/${t.template_id}`); toast.success("Deleted"); load();
  };

  if (editing) return <WorkflowEditor wf={editing} canWrite={canWrite} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="workflows-page">
      {copying && <CopyWorkflowDialog source={copying} onClose={() => setCopying(null)} onCopied={async (data) => { setCopying(null); await load(); setEditing(data); }} />}
      <PageHeader title="Workflow Templates" subtitle="Contract-to-close checklists that auto-generate tasks with deadlines."
        action={canWrite && (
          <div className="flex items-center gap-2">
            <button data-testid="export-workflows-btn" onClick={exportWorkflows} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors"><Download className="w-4 h-4" /> Export</button>
            <label data-testid="import-workflows-label" className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] transition-colors ${importing ? "opacity-60 cursor-wait" : "hover:bg-[#F0EFEC] cursor-pointer"}`}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {importing ? "Importing…" : "Import"}
              <input data-testid="import-workflows-input" type="file" accept=".json,application/json" hidden disabled={importing} onChange={importFile} />
            </label>
            <button data-testid="new-workflow-btn" onClick={() => setEditing({ name: "", description: "", task_groups: [{ name: "Tasks", tasks: [blankTask()] }] })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312]"><Plus className="w-4 h-4" /> New Workflow</button>
          </div>
        )} />
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="workflow-filters">
        {SIDE_TABS.map(([val, label]) => (
          <button key={val} data-testid={`filter-side-${val}`} onClick={() => setSideFilter(val)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${sideFilter === val ? "bg-[#1F3C88] text-white" : "bg-white border border-[#E5E5E2] text-[#575755] hover:bg-[#F0EFEC]"}`}>{label}</button>
        ))}
        <select data-testid="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="ml-1 px-3 py-1.5 rounded-full border border-[#E5E5E2] text-sm bg-white">
          <option value="all">All types</option>
          <option value="single_family">Single Family</option>
          <option value="condo">Condo</option>
          <option value="townhouse">Townhouse</option>
          <option value="multi_family">Multi-Family</option>
          <option value="land">Land</option>
        </select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.filter((w) => (sideFilter === "all" || w.transaction_side === sideFilter) && (typeFilter === "all" || w.property_type === typeFilter)).map((w) => {
          const count = (w.task_groups || []).reduce((n, g) => n + (g.tasks || []).length, 0);
          return (
            <div key={w.template_id} data-testid={`workflow-card-${w.template_id}`} className="bg-white border border-[#E5E5E2] rounded-sm p-5">
              <WorkflowIcon className="w-5 h-5 text-[#1F3C88] mb-3" />
              <div className="font-semibold text-[#0A0A0A]">{w.name}</div>
              {(w.transaction_side || w.property_type) && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {w.transaction_side && <span className="px-2 py-0.5 rounded-full bg-[#EAF0FB] text-[#1F3C88] text-[10px] font-semibold uppercase tracking-wide capitalize">{w.transaction_side}</span>}
                  {w.property_type && <span className="px-2 py-0.5 rounded-full bg-[#FDF0E2] text-[#B96A12] text-[10px] font-semibold uppercase tracking-wide capitalize">{w.property_type.replace("_", " ")}</span>}
                </div>
              )}
              <p className="text-sm text-[#575755] mt-2 line-clamp-2">{w.description}</p>
              <div className="text-xs text-[#8a8a86] mt-3">{count} tasks · {(w.task_groups || []).length} groups</div>
              <div data-testid={`workflow-usage-${w.template_id}`} className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-[11px] font-semibold ${w.active_count ? "bg-[#FBF2DE] text-[#B07D14]" : "bg-[#E7F0EA] text-[#2E5C47]"}`}>
                {w.active_count ? `In use · ${w.active_count} active transaction${w.active_count === 1 ? "" : "s"}` : "Not in use"}
              </div>
              {canWrite && (
                <div className="flex gap-2 mt-4">
                  <button data-testid={`edit-workflow-${w.template_id}`} onClick={() => setEditing(w)} className="flex-1 py-2 rounded-sm border border-[#E5E5E2] text-xs font-semibold text-[#1F3C88] hover:bg-[#F8FAFE]">Edit</button>
                  <button data-testid={`duplicate-workflow-${w.template_id}`} onClick={() => setCopying(w)} title="Copy this entire workflow" className="px-3 py-2 rounded-sm border border-[#E5E5E2] text-[#1F3C88] hover:bg-[#F8FAFE]"><Copy className="w-4 h-4" /></button>
                  <button data-testid={`delete-workflow-${w.template_id}`} onClick={() => del(w)} className="px-3 py-2 rounded-sm border border-[#E5E5E2] text-[#992B2B] hover:bg-[#F6E4E4]"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && <p className="text-sm text-[#8a8a86]">No workflows yet.</p>}
      </div>
    </div>
  );
}

function WorkflowEditor({ wf, canWrite, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...wf,
    task_groups: (wf.task_groups?.length ? wf.task_groups : [{ name: "Tasks", tasks: [blankTask()] }])
      .map((g) => ({ ...g, _k: g._k || uid(), tasks: (g.tasks || []).map((t) => ({ ...t, _k: t._k || uid() })) })),
  }));
  const [saving, setSaving] = useState(false);
  const setGroups = (groups) => setForm((f) => ({ ...f, task_groups: groups }));
  const setTask = (gi, ti, k, v) => setGroups(form.task_groups.map((g, i) => i !== gi ? g : { ...g, tasks: g.tasks.map((t, j) => j === ti ? { ...t, [k]: v } : t) }));
  const [taskDrag, setTaskDrag] = useState(null);
  const moveTaskTo = (gi, from, to) => setGroups(form.task_groups.map((g, i) => {
    if (i !== gi) return g;
    if (from === to || from < 0 || to < 0 || from >= g.tasks.length || to >= g.tasks.length) return g;
    const tasks = [...g.tasks];
    const [m] = tasks.splice(from, 1);
    tasks.splice(to, 0, m);
    return { ...g, tasks };
  }));
  const toggleRole = (gi, ti, field, key) => setGroups(form.task_groups.map((g, i) => i !== gi ? g : { ...g, tasks: g.tasks.map((t, j) => j !== ti ? t : { ...t, [field]: (t[field] || []).includes(key) ? (t[field] || []).filter((k) => k !== key) : [...(t[field] || []), key] }) }));
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroup = (gi) => setCollapsedGroups((c) => ({ ...c, [gi]: !c[gi] }));
  const allCollapsed = form.task_groups.length > 0 && form.task_groups.every((_, gi) => collapsedGroups[gi]);
  const toggleAllGroups = () => setCollapsedGroups(allCollapsed ? {} : Object.fromEntries(form.task_groups.map((_, gi) => [gi, true])));
  const moveGroup = (gi, dir) => {
    const nj = gi + dir;
    if (nj < 0 || nj >= form.task_groups.length) return;
    const groups = [...form.task_groups];
    [groups[gi], groups[nj]] = [groups[nj], groups[gi]];
    setGroups(groups);
    setCollapsedGroups((c) => ({ ...c, [gi]: c[nj], [nj]: c[gi] }));
  };

  // Group clipboard — copy a group from any workflow and paste it into another.
  const [clip, setClip] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("wf_group_clip") || "null"); } catch { return null; }
  });
  const copyGroup = (gi) => {
    const snap = JSON.parse(JSON.stringify(form.task_groups[gi]));
    try { sessionStorage.setItem("wf_group_clip", JSON.stringify(snap)); } catch { /* ignore */ }
    setClip(snap);
    toast.success(`Copied "${snap.name || "group"}" — paste it into any workflow`);
  };
  const pasteGroup = () => {
    if (!clip) return;
    const snap = JSON.parse(JSON.stringify(clip));
    snap._k = uid();
    snap.tasks = (snap.tasks || []).map((t) => ({ ...t, _k: uid() }));
    setGroups([...form.task_groups, snap]);
    toast.success(`Pasted "${snap.name || "group"}"`);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name the workflow"); return; }
    setSaving(true);
    const payload = {
      ...form,
      task_groups: form.task_groups.map(({ _k, tasks, ...g }) => ({
        ...g,
        tasks: (tasks || []).map(({ _k: _tk, ...t }) => t),
      })),
    };
    try {
      if (form.template_id) {
        const { data } = await api.put(`/tm/workflows/${form.template_id}`, payload);
        const n = data?.synced_transactions || 0;
        toast.success(n ? `Workflow saved — task order synced to ${n} active transaction${n === 1 ? "" : "s"}` : "Workflow saved");
      } else {
        await api.post("/tm/workflows", payload);
        toast.success("Workflow saved");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="workflow-editor" className="max-w-3xl">
      <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-[#575755] hover:text-[#1F3C88] mb-6"><ChevronLeft className="w-4 h-4" /> Back</button>
      <h1 className="font-serif text-3xl text-[#0A0A0A] mb-6">{form.template_id ? "Edit Workflow" : "New Workflow"}</h1>
      <div className="space-y-4 bg-white border border-[#E5E5E2] rounded-sm p-5">
        <div><span className={lbl}>Name</span><input data-testid="wf-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><span className={lbl}>Description</span><input className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
      </div>

      {form.task_groups.length > 1 && (
        <div className="mt-5 flex justify-end">
          <button type="button" data-testid="wf-toggle-all-groups" onClick={toggleAllGroups} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F3C88] hover:text-[#152A61]">
            {allCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        </div>
      )}

      {form.task_groups.map((g, gi) => (
        <div key={g._k} className="mt-5 bg-white border border-[#E5E5E2] rounded-sm p-5">
          <div onClick={() => toggleGroup(gi)} className="flex items-center gap-2 mb-4 cursor-pointer select-none">
            <button type="button" data-testid={`wf-group-collapse-${gi}`} onClick={(e) => { e.stopPropagation(); toggleGroup(gi); }} title={collapsedGroups[gi] ? "Expand group" : "Collapse group"} className="text-[#8a8a86] hover:text-[#1F3C88] shrink-0"><ChevronDown className={`w-4 h-4 transition-transform ${collapsedGroups[gi] ? "-rotate-90" : ""}`} /></button>
            <div className="flex flex-col shrink-0" onClick={(e) => e.stopPropagation()}>
              <button type="button" data-testid={`wf-group-up-${gi}`} onClick={(e) => { e.stopPropagation(); moveGroup(gi, -1); }} disabled={gi === 0} title="Move group up" className="text-[#8a8a86] hover:text-[#1F3C88] disabled:opacity-30 disabled:hover:text-[#8a8a86]"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" data-testid={`wf-group-down-${gi}`} onClick={(e) => { e.stopPropagation(); moveGroup(gi, 1); }} disabled={gi === form.task_groups.length - 1} title="Move group down" className="text-[#8a8a86] hover:text-[#1F3C88] disabled:opacity-30 disabled:hover:text-[#8a8a86]"><ChevronDown className="w-4 h-4" /></button>
            </div>
            <input className={`${inp} font-semibold`} onClick={(e) => e.stopPropagation()} value={g.name} onChange={(e) => setGroups(form.task_groups.map((x, i) => i === gi ? { ...x, name: e.target.value } : x))} placeholder="Group name" />
            <span className="shrink-0 text-xs font-semibold text-[#a3a099] tabular-nums px-1">{g.tasks.length} task{g.tasks.length === 1 ? "" : "s"}</span>
            <button type="button" data-testid={`wf-group-copy-${gi}`} onClick={(e) => { e.stopPropagation(); copyGroup(gi); }} title="Copy this group to paste into another workflow" className="text-[#8a8a86] hover:text-[#1F3C88] shrink-0"><Copy className="w-4 h-4" /></button>
            <button onClick={(e) => { e.stopPropagation(); setGroups(form.task_groups.filter((_, i) => i !== gi)); }} title="Delete group" className="text-[#8a8a86] hover:text-[#992B2B] shrink-0"><X className="w-4 h-4" /></button>
          </div>
          {!collapsedGroups[gi] && (
          <div className="space-y-3">
            {g.tasks.map((t, ti) => (
              <div key={t._k}
                onDragOver={(e) => { if (taskDrag && taskDrag.gi === gi && taskDrag.ti !== ti) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); if (taskDrag && taskDrag.gi === gi) moveTaskTo(gi, taskDrag.ti, ti); setTaskDrag(null); }}
                className={`border border-[#F0EFEC] rounded-sm p-3 ${taskDrag && taskDrag.gi === gi && taskDrag.ti === ti ? "opacity-40 ring-2 ring-[#F7941D]" : ""}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span draggable data-testid={`wf-task-drag-${gi}-${ti}`} onDragStart={(e) => { e.stopPropagation(); setTaskDrag({ gi, ti }); }} onDragEnd={() => setTaskDrag(null)} title="Drag to reorder" className="shrink-0 cursor-grab active:cursor-grabbing text-[#c7c4bd] hover:text-[#1F3C88] py-1"><GripVertical className="w-4 h-4" /></span>
                  <span className="shrink-0 text-xs font-semibold text-[#a3a099] tabular-nums w-5 text-center">{ti + 1}</span>
                  <input data-testid={`wf-task-title-${gi}-${ti}`} className={inp} placeholder="Task title" value={t.title} onChange={(e) => setTask(gi, ti, "title", e.target.value)} />
                  <button onClick={() => setGroups(form.task_groups.map((x, i) => i === gi ? { ...x, tasks: x.tasks.filter((_, j) => j !== ti) } : x))} className="text-[#8a8a86] hover:text-[#992B2B] shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
                  <input type="number" data-testid={`wf-task-days-${gi}-${ti}`} className={inp} value={t.days_offset} onChange={(e) => setTask(gi, ti, "days_offset", e.target.value)} title="Days offset" />
                  <select data-testid={`wf-task-dir-${gi}-${ti}`} className={inp} value={t.due_direction} onChange={(e) => setTask(gi, ti, "due_direction", e.target.value)}>
                    <option value="before">days before</option><option value="after">days after</option>
                  </select>
                  <select data-testid={`wf-task-ref-${gi}-${ti}`} className={inp} value={t.reference_date} onChange={(e) => setTask(gi, ti, "reference_date", e.target.value)}>
                    {REF_DATES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <div className="flex items-center gap-3 text-xs">
                    <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`wf-task-key-${gi}-${ti}`} checked={t.is_key_date} onChange={(e) => setTask(gi, ti, "is_key_date", e.target.checked)} className="accent-[#F7941D]" /><Star className="w-3.5 h-3.5" /> Key</label>
                    <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`wf-task-email-${gi}-${ti}`} checked={t.is_email} onChange={(e) => setTask(gi, ti, "is_email", e.target.checked)} className="accent-[#1F3C88]" /><Mail className="w-3.5 h-3.5" /> Email</label>
                  </div>
                </div>
                <div className="mt-2">
                  <span className={lbl}>Notes / Instructions</span>
                  <RichTextEditor testid={`wf-task-notes-${gi}-${ti}`} value={t.description || ""} onChange={(html) => setTask(gi, ti, "description", html)} placeholder="Internal notes or instructions for this task…" />
                </div>
                {t.is_email && (
                  <div className="mt-2 space-y-2 border-l-2 border-[#1F3C88] pl-3">
                    <RoleRow label="Send To" testid={`wf-task-to-${gi}-${ti}`} selected={t.email_to} onToggle={(k) => toggleRole(gi, ti, "email_to", k)} />
                    <RoleRow label="Cc — Transaction Parties" testid={`wf-task-cc-${gi}-${ti}`} selected={t.email_cc} onToggle={(k) => toggleRole(gi, ti, "email_cc", k)} />
                    <input data-testid={`wf-task-cc-extra-${gi}-${ti}`} className={inp} placeholder="Additional Cc emails (comma-separated)" value={t.email_cc_extra || ""} onChange={(e) => setTask(gi, ti, "email_cc_extra", e.target.value)} />
                    <RoleRow label="Bcc — Transaction Parties" testid={`wf-task-bcc-${gi}-${ti}`} selected={t.email_bcc} onToggle={(k) => toggleRole(gi, ti, "email_bcc", k)} />
                    <input data-testid={`wf-task-bcc-extra-${gi}-${ti}`} className={inp} placeholder="Additional Bcc emails (comma-separated)" value={t.email_bcc_extra || ""} onChange={(e) => setTask(gi, ti, "email_bcc_extra", e.target.value)} />
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`${lbl} mb-0`}>Email Subject</span>
                        <MergeMenu align="right" onPick={(tok) => setTask(gi, ti, "email_subject", (t.email_subject || "") + tok)} />
                      </div>
                      <input data-testid={`wf-task-email-subject-${gi}-${ti}`} className={inp} placeholder="Subject — supports {{merge_fields}}" value={t.email_subject || ""} onChange={(e) => setTask(gi, ti, "email_subject", e.target.value)} />
                    </div>
                    <div>
                      <span className={lbl}>Email Body</span>
                      <RichTextEditor testid={`wf-task-email-body-${gi}-${ti}`} value={t.email_body || ""} onChange={(html) => setTask(gi, ti, "email_body", html)} placeholder="Message sent when this task's email fires…" mergeFields />
                    </div>
                    <p className="text-[11px] text-[#a3a099]">Tip: tokens like <code>{"{{buyer_name}}"}</code> or <code>{"{{closing_date}}"}</code> fill in per transaction.</p>
                  </div>
                )}
              </div>
            ))}
            <button data-testid={`wf-add-task-${gi}`} onClick={() => setGroups(form.task_groups.map((x, i) => i === gi ? { ...x, tasks: [...x.tasks, blankTask()] } : x))} className="inline-flex items-center gap-1.5 text-sm text-[#1F3C88] font-semibold hover:text-[#152A61]"><Plus className="w-4 h-4" /> Add task</button>
          </div>
          )}
        </div>
      ))}

      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button data-testid="wf-add-group" onClick={() => setGroups([...form.task_groups, { _k: uid(), name: "New Group", tasks: [blankTask()] }])} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5E2] text-sm font-semibold hover:bg-[#F0EFEC]"><Plus className="w-4 h-4" /> Add group</button>
          {clip && <button data-testid="wf-paste-group" onClick={pasteGroup} title="Paste the copied group into this workflow" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#1F3C88] text-sm font-semibold text-[#1F3C88] hover:bg-[#EAF0FB] max-w-[260px]"><ClipboardPaste className="w-4 h-4 shrink-0" /> <span className="truncate">Paste "{clip.name || "group"}"</span></button>}
        </div>
        <button data-testid="wf-save-btn" onClick={save} disabled={saving || !canWrite} className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] disabled:opacity-60">{saving ? "Saving…" : "Save Workflow"}</button>
      </div>
    </div>
  );
}
